import { useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Calculator,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Home,
  MapPin,
  Menu,
  MoreHorizontal,
  Sparkles,
  Sun,
  X,
} from 'lucide-react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { getBreadcrumbItems } from '../lib/breadcrumbs';
import { PrivacyChoices } from './PrivacyChoices';
import { getConsentState } from '../lib/consent';
import { useLocale } from '../context/LocaleContext';
import { site } from '../lib/site';
import { MonetagController } from './MonetagController';
import { lockAdvertising, unlockAdvertising } from '../lib/monetag';
const desktopNav = [
  ['nav.home', '/'],
  ['nav.calculatorsShort', '/calculators'],
  ['nav.assessmentShort', '/solar-assessment'],
  ['nav.solarPlanShort', '/my-solar-plan'],
  ['nav.installersShort', '/solar-companies'],
  ['nav.checklistShort', '/installer-checklist'],
  ['nav.decisionCenterShort', '/solar-decision-center-philippines'],
] as const;
const drawerNav = [
  ['nav.home', '/'],
  ['nav.calculators', '/calculators'],
  ['nav.assessment', '/solar-assessment'],
  ['nav.decisionCenter', '/solar-decision-center-philippines'],
  ['nav.solarPlan', '/my-solar-plan'],
  ['nav.guides', '/guides'],
  ['nav.caseStudies', '/case-studies'],
  ['nav.installers', '/solar-companies'],
  ['nav.checklist', '/installer-checklist'],
  ['nav.methodology', '/methodology'],
  ['nav.about', '/about'],
] as const;
function Logo({
  inverse = false,
  compact = false,
  showTagline = false,
}: {
  inverse?: boolean;
  compact?: boolean;
  showTagline?: boolean;
}) {
  const { t } = useLocale();
  return (
    <Link
      to="/"
      className="inline-flex min-w-0 shrink-0 items-center gap-2.5"
      aria-label={t('nav.home')}
    >
      <img
        src="/brand/solargabay-mark.png"
        alt=""
        className={compact ? 'h-9 w-9 object-contain sm:h-10 sm:w-10' : 'h-11 w-11 object-contain'}
      />
      <span className={compact ? 'hidden min-[350px]:block' : 'block'}>
        <strong
          className={`block whitespace-nowrap font-black leading-none tracking-[-0.045em] ${inverse ? 'text-white' : 'text-navy-950'} ${compact ? 'text-[1.05rem] sm:text-lg' : 'text-xl sm:text-2xl'}`}
        >
          Solar<span className={inverse ? 'text-sun-400' : 'text-save-600'}>Gabay</span>{' '}
          <span
            className={`rounded-md px-1.5 py-0.5 align-middle text-[.56em] tracking-normal ${inverse ? 'bg-white text-navy-950' : 'bg-navy-900 text-white'}`}
          >
            PH
          </span>
        </strong>
        {showTagline && (
          <small
            className={`mt-1 block text-[11px] font-bold ${inverse ? 'text-slate-300' : 'text-slate-500'}`}
          >
            {t('brand.tagline')}
          </small>
        )}
      </span>
    </Link>
  );
}
function DesktopLearnMenu({ open, setOpen }: { open: boolean; setOpen: (value: boolean) => void }) {
  const { t } = useLocale();
  const location = useLocation();
  const active =
    location.pathname.startsWith('/guides') ||
    location.pathname.startsWith('/case-studies') ||
    location.pathname === '/methodology';
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen]);
  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className={`relative flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-2 text-[11px] font-extrabold min-[1440px]:px-2.5 min-[1440px]:text-[12px] transition after:absolute after:inset-x-3 after:-bottom-[0.72rem] after:h-0.5 after:rounded-full after:bg-sun-400 ${active || open ? 'text-white after:opacity-100' : 'text-slate-200 after:opacity-0 hover:bg-white/10 hover:text-white'}`}
      >
        {t('nav.learnShort')}
        <ChevronDown className={`h-3.5 w-3.5 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-[calc(100%+1rem)] z-20 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white p-2 text-navy-950 shadow-2xl"
          role="menu"
        >
          <NavLink
            to="/guides"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-extrabold ${isActive ? 'bg-navy-950 text-white' : 'hover:bg-navy-50'}`
            }
          >
            <BookOpen className="h-4 w-4" />
            {t('nav.guides')}
          </NavLink>
          <NavLink
            to="/case-studies"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-extrabold ${isActive ? 'bg-navy-950 text-white' : 'hover:bg-navy-50'}`
            }
          >
            <Sparkles className="h-4 w-4" />
            {t('nav.caseStudies')}
          </NavLink>
          <NavLink
            to="/methodology"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-extrabold ${isActive ? 'bg-navy-950 text-white' : 'hover:bg-navy-50'}`
            }
          >
            <ClipboardCheck className="h-4 w-4" />
            {t('nav.methodology')}
          </NavLink>
        </div>
      )}
    </div>
  );
}
export function Breadcrumbs() {
  const location = useLocation();
  const { t } = useLocale();
  if (location.pathname === '/') return null;
  const items = getBreadcrumbItems(location.pathname);
  if (!items.length) return null;
  return (
    <nav aria-label={'Breadcrumb'} className="container-page py-3 sm:py-4">
      <ol className="flex flex-wrap items-center gap-1 text-xs font-bold text-slate-500 sm:text-sm">
        <li>
          <Link to="/" className="transition hover:text-navy-900">
            {t('common.home')}
          </Link>
        </li>
        {items.map((item, index) => (
          <li key={`${item.path ?? item.label}-${index}`} className="flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
            {!item.path ? (
              <span aria-current="page" className="text-navy-800">
                {item.label}
              </span>
            ) : (
              <Link to={item.path} className="transition hover:text-navy-900">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
export function AppLayout() {
  const { t } = useLocale();
  const [drawer, setDrawer] = useState(false);
  const [learnMenuOpen, setLearnMenuOpen] = useState(false);
  const [privacyChoicesOpen, setPrivacyChoicesOpen] = useState(false);
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);
  const openPrivacyChoices = () => setPrivacyChoicesOpen(true);
  useEffect(() => {
    const consent = getConsentState();
    if (consent.choice === null && consent.region !== 'nonregulated') setPrivacyChoicesOpen(true);
  }, []);
  useEffect(() => {
    if (drawer) lockAdvertising('mobile-navigation');
    else unlockAdvertising('mobile-navigation');
    document.body.classList.toggle('menu-open', drawer);
    if (drawer) closeButtonRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawer(false);
      if (event.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), select:not([disabled])',
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('menu-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [drawer]);
  useEffect(() => {
    if (privacyChoicesOpen) lockAdvertising('modal-open');
    else unlockAdvertising('modal-open');
    return () => unlockAdvertising('modal-open');
  }, [privacyChoicesOpen]);
  useEffect(() => {
    setDrawer(false);
    setLearnMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);
  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap rounded-md px-2 py-2 text-[11px] font-extrabold min-[1440px]:px-2.5 min-[1440px]:text-[12px] transition after:absolute after:inset-x-3 after:-bottom-[0.72rem] after:h-0.5 after:rounded-full after:bg-sun-400 ${isActive ? 'text-white after:opacity-100' : 'text-slate-200 after:opacity-0 hover:bg-white/10 hover:text-white'}`;
  return (
    <div className="min-h-screen pb-16 md:pb-0">
      <MonetagController />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-xl focus:bg-white focus:px-4 focus:py-3 focus:font-black focus:shadow-lift"
      >
        {t('nav.skip')}
      </a>

      <header
        className="site-header ad-excluded-area sticky inset-x-0 top-0 z-50 border-b border-white/10 bg-navy-950 text-white shadow-[0_8px_28px_rgba(7,20,38,.2)]"
        data-ad-exclude="true"
      >
        <div className="mx-auto flex min-h-[60px] w-full max-w-[110rem] items-center gap-3 px-4 py-2 sm:px-6 lg:px-8">
          <Logo inverse compact />

          <nav
            className="hidden min-w-0 flex-1 items-center justify-center gap-0 min-[1536px]:flex"
            aria-label={t('nav.primary')}
          >
            {desktopNav.slice(0, 4).map(([labelKey, href]) => (
              <NavLink key={href} to={href} end={href === '/'} className={navClass}>
                {t(labelKey)}
              </NavLink>
            ))}
            <DesktopLearnMenu open={learnMenuOpen} setOpen={setLearnMenuOpen} />
            {desktopNav.slice(4).map(([labelKey, href]) => (
              <NavLink key={href} to={href} className={navClass}>
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Link
              to="/solar-assessment"
              className="btn-primary hidden min-h-9 rounded-md px-3 py-2 text-[11px] min-[1700px]:inline-flex"
            >
              <Sparkles className="h-4 w-4" />
              {t('nav.startShort')}
            </Link>
            <button
              type="button"
              aria-label={t('nav.openMenu')}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-white/20 bg-white/10 text-white min-[1536px]:hidden"
              onClick={() => setDrawer(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {drawer && (
        <div
          className="fixed inset-0 z-[60] bg-navy-950/65 backdrop-blur-sm min-[1536px]:hidden"
          role="presentation"
          onClick={() => setDrawer(false)}
        >
          <aside
            ref={drawerRef}
            className="ml-auto flex h-dvh w-[min(92vw,390px)] flex-col overflow-y-auto bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.siteMenu')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <Logo compact />
              <button
                type="button"
                aria-label={t('nav.closeMenu')}
                ref={closeButtonRef}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white"
                onClick={() => setDrawer(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="mt-5 grid gap-1">
              {drawerNav.map(([labelKey, href]) => (
                <NavLink
                  key={href}
                  to={href}
                  end={href === '/'}
                  className={({ isActive }) =>
                    `min-h-11 rounded-lg px-4 py-2.5 text-sm font-black ${isActive ? 'bg-navy-950 text-white' : 'text-navy-900 hover:bg-slate-100'}`
                  }
                >
                  {t(labelKey)}
                </NavLink>
              ))}
            </nav>

            <div className="mt-auto grid gap-3 pt-7">
              <Link to="/calculators" className="btn-primary w-full">
                <Calculator className="h-5 w-5" />
                {t('action.openCalculators')}
              </Link>
              <Link to="/solar-companies" className="btn-secondary w-full">
                {t('action.findInstallers')}
              </Link>
            </div>
          </aside>
        </div>
      )}

      <main id="main-content">
        <Outlet />
      </main>

      <footer
        className="ad-excluded-area mt-16 overflow-hidden bg-navy-950 text-white"
        data-ad-exclude="true"
      >
        <div className="border-b border-white/10 bg-[radial-gradient(circle_at_85%_20%,rgba(245,196,0,.18),transparent_18rem)]">
          <div className="container-page grid gap-7 py-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.14em] text-sun-400">
                {t('footer.kicker')}
              </p>
              <h2 className="mt-3 max-w-3xl text-3xl font-black leading-tight tracking-[-0.035em] sm:text-4xl">
                {t('footer.title')}
              </h2>
            </div>
            <div className="grid gap-3 sm:flex">
              <Link to="/calculators" className="btn-primary">
                <Calculator className="h-5 w-5" />
                {t('action.openCalculators')}
              </Link>
              <Link
                to="/solar-companies"
                className="btn-secondary border-white/25 bg-white/10 text-white hover:bg-white/15"
              >
                {t('action.findInstallers')}
              </Link>
            </div>
          </div>
        </div>
        <div className="container-page grid gap-8 py-10 sm:grid-cols-2 lg:grid-cols-[1.35fr_.75fr_.75fr_.75fr]">
          <div>
            <Logo inverse showTagline />
            <p className="mt-5 max-w-md text-sm leading-7 text-slate-300">
              {t('footer.description')}
            </p>
            <p className="mt-4 text-sm font-black text-sun-400">{t('footer.motto')}</p>
          </div>
          <div>
            <p className="font-black">{t('footer.plan')}</p>
            <div className="mt-4 grid gap-2.5 text-sm text-slate-300">
              <Link to="/calculators/solar">{t('footer.solarCalculators')}</Link>
              <Link to="/calculators/electricity">{t('footer.electricityCalculators')}</Link>
              <Link to="/solar-assessment">{t('nav.assessment')}</Link>
              <Link to="/solar-decision-center-philippines">{t('nav.decisionCenter')}</Link>
              <Link to="/solar-quote-checker-philippines">{t('nav.quoteChecker')}</Link>
              <Link to="/philippine-solar-data-explorer">{t('nav.dataExplorer')}</Link>
              <Link to="/my-solar-plan">{t('nav.solarPlan')}</Link>
              <Link to="/installer-checklist">{t('nav.checklist')}</Link>
            </div>
          </div>
          <div>
            <p className="font-black">{t('footer.learn')}</p>
            <div className="mt-4 grid gap-2.5 text-sm text-slate-300">
              <Link to="/guides">{t('footer.guides')}</Link>
              <Link to="/methodology">{t('nav.methodology')}</Link>
              <Link to="/sources">{t('footer.sources')}</Link>
              <Link to="/corrections">{t('footer.corrections')}</Link>
            </div>
          </div>
          <div>
            <p className="font-black">{t('footer.company')}</p>
            <div className="mt-4 grid gap-2.5 text-sm text-slate-300">
              <Link to="/about">{t('nav.about')}</Link>
              <Link to="/contact">{t('footer.contact')}</Link>
              <Link to="/privacy">{t('footer.privacy')}</Link>
              <button
                id="privacy-choices"
                type="button"
                className="min-h-11 text-left underline decoration-white/40 underline-offset-4"
                onClick={openPrivacyChoices}
              >
                Privacy choices
              </button>
              <Link to="/terms">{t('footer.terms')}</Link>
              <Link to="/advertise">{t('footer.advertise')}</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="container-page flex flex-col gap-2 py-5 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
            <span>© 2026 SolarGabay PH · Build {site.buildId}</span>
            <span>{t('footer.disclaimer')}</span>
          </div>
        </div>
      </footer>

      <nav
        className="mobile-bottom-nav ad-excluded-area fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-white/10 bg-navy-950 pb-[env(safe-area-inset-bottom)] text-white shadow-[0_-8px_30px_rgba(7,20,38,.18)] md:hidden"
        data-ad-exclude="true"
        aria-label={t('nav.mobile')}
      >
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `flex min-h-14 flex-col items-center justify-center gap-1 px-1 text-center text-[9px] font-black leading-3 min-[350px]:min-h-16 ${isActive ? 'text-sun-400' : 'text-slate-300'}`
          }
        >
          <Home className="h-5 w-5" />
          <span className="max-[349px]:sr-only">{t('nav.home')}</span>
        </NavLink>
        <NavLink
          to="/calculators"
          className={({ isActive }) =>
            `flex min-h-14 flex-col items-center justify-center gap-1 px-1 text-center text-[9px] font-black leading-3 min-[350px]:min-h-16 ${isActive ? 'text-sun-400' : 'text-slate-300'}`
          }
        >
          <Calculator className="h-5 w-5" />
          <span className="max-[349px]:sr-only">{t('nav.calculatorsShort')}</span>
        </NavLink>
        <NavLink
          to="/solar-assessment"
          className={({ isActive }) =>
            `flex min-h-14 flex-col items-center justify-center gap-1 px-1 text-center text-[9px] font-black leading-3 min-[350px]:min-h-16 ${isActive ? 'text-sun-400' : 'text-slate-300'}`
          }
        >
          <Sun className="h-5 w-5" />
          <span className="max-[349px]:sr-only">{t('nav.assessmentShort')}</span>
        </NavLink>
        <NavLink
          to="/solar-companies"
          className={({ isActive }) =>
            `flex min-h-14 flex-col items-center justify-center gap-1 px-1 text-center text-[9px] font-black leading-3 min-[350px]:min-h-16 ${isActive ? 'text-sun-400' : 'text-slate-300'}`
          }
        >
          <MapPin className="h-5 w-5" />
          <span className="max-[349px]:sr-only">{t('nav.installersShort')}</span>
        </NavLink>
        <button
          type="button"
          className="flex min-h-14 flex-col items-center justify-center gap-1 px-1 text-center text-[9px] font-black leading-3 min-[350px]:min-h-16 text-slate-300"
          onClick={() => setDrawer(true)}
          aria-label={t('nav.more')}
        >
          <MoreHorizontal className="h-5 w-5" />
          <span className="max-[349px]:sr-only">{t('nav.more')}</span>
        </button>
      </nav>
      <PrivacyChoices open={privacyChoicesOpen} onClose={() => setPrivacyChoicesOpen(false)} />
    </div>
  );
}
export const Layout = AppLayout;
